import type { UseFormRegister } from "react-hook-form"
import type { AccountAddressFormData } from "@/api/account-addresses"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

interface AddressFormFieldsProps {
  mode: "billing" | "shipping"
  register: UseFormRegister<AccountAddressFormData>
}

export function AddressFormFields({ mode, register }: AddressFormFieldsProps) {
  const prefix = mode === "billing" ? "billing" : "shipping"
  const isBilling = mode === "billing"

  return (
    <>
      {isBilling && (
        <div className='space-y-2'>
          <Label htmlFor={`${prefix}-email`}>
            Endereco de e-mail <span className='text-destructive'>*</span>
          </Label>
          <Input
            className='h-11 bg-white'
            id={`${prefix}-email`}
            type='email'
            {...register("email")}
          />
        </div>
      )}

      <div className='space-y-2'>
        <Label htmlFor={`${prefix}-first-name`}>
          Nome <span className='text-destructive'>*</span>
        </Label>
        <Input
          className='h-11 bg-white'
          id={`${prefix}-first-name`}
          {...register("firstName")}
        />
      </div>

      <div className='space-y-2'>
        <Label htmlFor={`${prefix}-last-name`}>
          Sobrenome <span className='text-destructive'>*</span>
        </Label>
        <Input
          className='h-11 bg-white'
          id={`${prefix}-last-name`}
          {...register("lastName")}
        />
      </div>

      {isBilling ? (
        <>
          <div className='space-y-2'>
            <Label htmlFor={`${prefix}-cpf`}>
              CPF <span className='text-destructive'>*</span>
            </Label>
            <Input
              className='h-11 bg-white'
              id={`${prefix}-cpf`}
              placeholder='CPF'
              {...register("cpf")}
            />
          </div>

          <div className='space-y-2'>
            <Label htmlFor={`${prefix}-phone`}>
              Telefone <span className='text-destructive'>*</span>
            </Label>
            <Input
              className='h-11 bg-white'
              id={`${prefix}-phone`}
              type='tel'
              {...register("phone")}
            />
          </div>

          <div className='space-y-2'>
            <Label htmlFor={`${prefix}-zip`}>
              CEP <span className='text-destructive'>*</span>
            </Label>
            <Input
              className='h-11 bg-white'
              id={`${prefix}-zip`}
              {...register("zipCode")}
            />
          </div>
        </>
      ) : (
        <>
          <div className='space-y-2'>
            <Label htmlFor={`${prefix}-zip`}>
              CEP <span className='text-destructive'>*</span>
            </Label>
            <Input
              className='h-11 bg-white'
              id={`${prefix}-zip`}
              {...register("zipCode")}
            />
          </div>

          <div className='space-y-2'>
            <Label htmlFor={`${prefix}-phone`}>
              Telefone <span className='text-destructive'>*</span>
            </Label>
            <Input
              className='h-11 bg-white'
              id={`${prefix}-phone`}
              type='tel'
              {...register("phone")}
            />
          </div>

          <div className='space-y-2'>
            <Label htmlFor={`${prefix}-cpf`}>
              CPF <span className='text-destructive'>*</span>
            </Label>
            <Input
              className='h-11 bg-white'
              id={`${prefix}-cpf`}
              {...register("cpf")}
            />
          </div>
        </>
      )}

      <div className='space-y-2'>
        <Label htmlFor={`${prefix}-street`}>
          Rua <span className='text-destructive'>*</span>
        </Label>
        <Input
          className='h-11 bg-white'
          id={`${prefix}-street`}
          placeholder='Nome da rua'
          {...register("street")}
        />
      </div>

      <div className='space-y-2'>
        <Label htmlFor={`${prefix}-number`}>
          Numero <span className='text-destructive'>*</span>
        </Label>
        <Input
          className='h-11 bg-white'
          id={`${prefix}-number`}
          {...register("number")}
        />
      </div>

      <div className='space-y-2'>
        <Label htmlFor={`${prefix}-complement`}>Complemento (opcional)</Label>
        <Input
          className='h-11 bg-white'
          id={`${prefix}-complement`}
          placeholder='Apartamento, suite, unidade, etc. (opcional)'
          {...register("complement")}
        />
      </div>

      <div className='space-y-2'>
        <Label htmlFor={`${prefix}-district`}>
          Bairro <span className='text-destructive'>*</span>
        </Label>
        <Input
          className='h-11 bg-white'
          id={`${prefix}-district`}
          {...register("district")}
        />
      </div>

      <div className='space-y-2'>
        <Label htmlFor={`${prefix}-city`}>
          Cidade <span className='text-destructive'>*</span>
        </Label>
        <Input
          className='h-11 bg-white'
          id={`${prefix}-city`}
          {...register("city")}
        />
      </div>

      <div className='space-y-2'>
        <Label htmlFor={`${prefix}-state`}>
          Estado <span className='text-destructive'>*</span>
        </Label>
        <select
          className='flex h-11 w-full min-w-0 rounded-md border border-input bg-white px-3 py-1 text-sm shadow-xs outline-none transition-[color,box-shadow] placeholder:text-muted-foreground/70 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50'
          id={`${prefix}-state`}
          {...register("state")}
        >
          <option value='AC'>Acre</option>
          <option value='AL'>Alagoas</option>
          <option value='AM'>Amazonas</option>
          <option value='AP'>Amapa</option>
          <option value='BA'>Bahia</option>
          <option value='CE'>Ceara</option>
          <option value='DF'>Distrito Federal</option>
          <option value='ES'>Espirito Santo</option>
          <option value='GO'>Goias</option>
          <option value='MA'>Maranhao</option>
          <option value='MG'>Minas Gerais</option>
          <option value='MS'>Mato Grosso do Sul</option>
          <option value='MT'>Mato Grosso</option>
          <option value='PA'>Para</option>
          <option value='PB'>Paraiba</option>
          <option value='PE'>Pernambuco</option>
          <option value='PI'>Piaui</option>
          <option value='PR'>Parana</option>
          <option value='RJ'>Rio de Janeiro</option>
          <option value='RN'>Rio Grande do Norte</option>
          <option value='RO'>Rondonia</option>
          <option value='RR'>Roraima</option>
          <option value='RS'>Rio Grande do Sul</option>
          <option value='SC'>Santa Catarina</option>
          <option value='SE'>Sergipe</option>
          <option value='SP'>Sao Paulo</option>
          <option value='TO'>Tocantins</option>
        </select>
      </div>

      <div className='space-y-1'>
        <p className='font-medium text-sm'>
          Pais <span className='text-destructive'>*</span>
        </p>
        <p className='text-base'>Brasil</p>
      </div>
    </>
  )
}
